// frontend/src/pages/contactpage/components/ConsultationBooking.jsx

import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { API_BASE_URL } from '../../../utils/api';

const ConsultationBooking = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ success: null, message: '' });

  const consultationTypes = [
    {
      id: 'discovery',
      title: 'Discovery Call',
      duration: '30 min',
      price: 'Free',
      description: 'Perfect for initial project discussions and getting to know each other.',
      features: ['Project scope discussion', 'Timeline & budget overview', 'Technical requirements', 'Next steps planning']
    },
    {
      id: 'technical',
      title: 'Technical Consultation',
      duration: '2 hrs',
      price: '$50',
      description: 'Deep dive into technical challenges and architecture decisions.',
      features: ['Code review & optimization', 'Architecture planning', 'Technology recommendations', 'Performance analysis']
    },
    {
      id: 'mentorship',
      title: 'Mentorship Session',
      duration: 'A week',
      price: '$100',
      description: 'Career guidance and skill development for aspiring developers.',
      features: ['Career roadmap planning', 'Skill assessment', 'Learning resources', 'Industry insights']
    }
  ];

  const availableDates = [
    { date: '2026-01-15', day: 'Wed', slots: ['09:00', '11:00', '14:00', '16:00'] },
    { date: '2025-01-16', day: 'Thu', slots: ['10:00', '13:00', '15:00'] },
    { date: '2025-01-17', day: 'Fri', slots: ['09:00', '11:00', '14:00'] },
    { date: '2025-01-20', day: 'Mon', slots: ['09:00', '11:00', '13:00', '15:00'] },
    { date: '2025-01-21', day: 'Tue', slots: ['10:00', '14:00', '16:00'] },
    { date: '2025-01-22', day: 'Wed', slots: ['09:00', '11:00', '13:00'] }
  ];

  const handleBooking = async () => {
    setFormStatus({ success: null, message: '' });

    if (!consultationType || !selectedDate || !selectedTime || !name || !email) {
      setFormStatus({ success: false, message: 'Please fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/book-consultation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          consultationType,
          selectedDate,
          selectedTime,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setFormStatus({ success: true, message: result.message });
        // Reset form
        setName('');
        setEmail('');
        setConsultationType('');
        setSelectedDate('');
        setSelectedTime('');
      } else {
        setFormStatus({ success: false, message: result.error || 'An unexpected error occurred.' });
      }
    } catch (error) {
      setFormStatus({ success: false, message: 'Failed to connect to the server. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBookingSummary = () => {
    if (!consultationType || !selectedDate || !selectedTime) return null;
    const selectedConsultation = consultationTypes?.find(type => type?.id === consultationType);
    const formattedDate = new Date(selectedDate)?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    return (
      <div className="bg-muted/50 rounded-lg p-6 mb-6">
        <h4 className="font-medium text-foreground mb-3">Booking Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Session:</span>
            <span className="text-foreground font-medium">{selectedConsultation?.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date:</span>
            <span className="text-foreground">{formattedDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Time:</span>
            <span className="text-foreground">{selectedTime} EST</span>
          </div>
          <div className="flex justify-between border-t border-border pt-2 mt-2">
            <span className="text-muted-foreground">Price:</span>
            <span className="text-foreground font-bold">{selectedConsultation?.price}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Book a Consultation
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Schedule a personalized session to discuss your project, get technical guidance, or receive mentorship.
          </p>
        </div>

        {formStatus.success !== null && (
          <div className={`p-4 mb-8 text-center rounded-lg ${formStatus.success ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
            {formStatus.message}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Consultation Types */}
          <div className="lg:col-span-1">
            <h3 className="text-xl font-semibold text-foreground mb-6">Choose Your Session</h3>
            <div className="space-y-4">
              {consultationTypes?.map((type) => (
                <div
                  key={type?.id}
                  onClick={() => setConsultationType(type?.id)}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                    consultationType === type?.id
                      ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-foreground">{type?.title}</h4>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">{type?.duration}</div>
                      <div className={`font-bold ${type?.price === 'Free' ? 'text-success' : 'text-primary'}`}>
                        {type?.price}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{type?.description}</p>
                  <ul className="space-y-1">
                    {type?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-success" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Calendar & Booking */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl border border-border p-6 lg:p-8">
              <h3 className="text-xl font-semibold text-foreground mb-6">Select Date & Time</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Input
                  label="Your Name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Input
                  label="Your Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {/* Date Selection
              <div className="mb-8">
                <h4 className="font-medium text-foreground mb-4">Available Dates</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {availableDates?.map((dateOption) => (
                    <button
                      key={dateOption?.date}
                      onClick={() => {
                        setSelectedDate(dateOption?.date);
                        setSelectedTime(''); // Reset time when date changes
                      }}
                      className={`p-4 rounded-lg border-2 text-center transition-all duration-300 ${
                        selectedDate === dateOption?.date
                          ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium text-foreground">{dateOption?.day}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(dateOption.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="text-xs text-success mt-1">
                        {dateOption?.slots?.length} slots
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              {/* {selectedDate && (
                <div className="mb-8">
                  <h4 className="font-medium text-foreground mb-4">Available Times</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {availableDates?.find(d => d?.date === selectedDate)
                      ?.slots?.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`p-3 rounded-lg border-2 text-center transition-all duration-300 ${
                            selectedTime === time
                              ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="font-medium text-foreground">{time}</div>
                          <div className="text-xs text-muted-foreground">EST</div>
                        </button>
                      ))}
                  </div>
                </div>
              )} */} 

              {/* Custom Date & Time Selection - New Section */}
              <h4 className="font-semibold text-foreground mb-4 mt-8">Choose Your perfect Date and Time</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-muted-foreground mb-2">Select a Date</label>
                  <input
                    id="date"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]} // Prevents selecting past dates
                    className="w-full p-3 rounded-md bg-background border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-muted-foreground mb-2">Select a Time</label>
                  <input
                    id="time"
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full p-3 rounded-md bg-background border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {renderBookingSummary()}

              {/* Book Button */}
              <Button
                onClick={handleBooking}
                loading={isSubmitting}
                disabled={!name || !email || !consultationType || !selectedDate || !selectedTime}
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                className="btn-conversion"
              >
                Confirm Booking
              </Button>

              {/* Additional Info */}
              <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-primary mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">What to expect:</p>
                    <ul className="space-y-1">
                      <li>• You'll receive a calendar invite with Zoom/Google Meet link</li>
                      <li>• Please prepare any questions or materials in advance</li>
                      <li>• Sessions can be rescheduled up to 24 hours before</li>
                      <li>• All consultations include follow-up notes and resources</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;